/**
 * Per-vessel-type geometry — one shared, profile-driven model instead of a
 * hardcoded shape per vessel. Every shape (flask's cone+neck, beaker's near-
 * cylinder, round-flask's sphere+neck, test-tube's cylinder+hemisphere) is
 * just a `radiusAt(t)` — a sphere included (radius = sqrt(r² - y²)) — so no
 * renderer ever needs shape-specific code: vessel2D.ts samples the same
 * function into a flat silhouette. Adding a vessel #5 later means adding one
 * profile here, not a new component.
 *
 * Convention: LOCAL space, floor at y=0, rim at y=height (not centered —
 * this way every vessel's floor sits at the same bench Y regardless of its
 * own height).
 */
import { WORKING_VOLUME_ML, type VesselType } from './vesselTypes';

/**
 * How full a vessel is drawn, as a share of the level it used to be drawn at.
 *
 * One number rather than four edited literals, because it is one judgement:
 * how much glassware a school pours into. A beaker filled two thirds of the
 * way up reads as a full glass of something; the same beaker a third full
 * reads as an experiment, and it leaves the headroom every other thing on the
 * bench needs — a rising level to watch, fumes over the surface, a delivery
 * tube's mouth, the spare depth a boiling mixture climbs into.
 */
const FILL_SHARE = 0.6;

export interface VesselProfile {
  height: number;
  /** Where the liquid stands at the vessel's WORKING volume, as a fraction of
   *  its height, scaled by FILL_SHARE. It was once the only level a vessel
   *  had; it is now the anchor the rest are measured from — see
   *  vesselTypes.ts's VesselCapacity and chemistry-lab-dosing-target.md. */
  fillT: number;
  /** True for a vessel with a genuine flat bottom (flask, beaker) — needs a
   *  separate floor-cap mesh. False for a rounded bottom (round-flask,
   *  test-tube) — the Lathe profile closes to a pole (radius → 0) on its
   *  own, so no extra mesh is needed there. */
  hasFlatBase: boolean;
  /** Radius the stand should be sized to (base radius for a flat-bottomed
   *  vessel; the widest/equator radius for a cradled one). */
  standRadius: number;
  /** The widest radius anywhere on the profile — how far out a rod has to
   *  stand to clear the glassware. */
  maxRadius: number;
  /** Height fraction a clamp grips the vessel at: a necked vessel is held by
   *  its neck, a straight-sided one around its body. */
  gripT: number;
  /** Height fraction of the ground-glass collar near the mouth — the grey
   *  band real flasks carry, and the detail that most says "laboratory
   *  glassware" rather than "a shape". Null for a vessel with none (a beaker
   *  has a plain lip and a spout instead). */
  collarT: number | null;
  /** Extra headroom above the rim for the HTML label. */
  labelOffset: number;
  radiusAt(t: number): number;
  /** The one feature a body of revolution cannot express, and so the one that
   *  cannot live in radiusAt: a pouring spout. It is a ONE-SIDED outward bulge
   *  in the rim — not a shape parked beside it, which is what every attempt to
   *  bolt a separate nub onto the mouth ends up looking like. Null for a
   *  vessel with no spout. */
  spout: Spout | null;
}

export interface Spout {
  /** Where it sits, in radians round the rim: 0 is the viewer's right, PI/2
   *  the near side, PI the left. */
  angle: number;
  /** How far the rim's OUTER edge is drawn out at the centre of the bulge, in
   *  profile units. Needs to be far larger than it sounds — it is measured
   *  against a radius it sits flush against, so anything subtle simply
   *  disappears into the rim it grew out of. */
  bump: number;
  /** How far the bore's edge follows it out, as a fraction of `bump`. Below 1
   *  the band widens through the beak, which is what opens the pouring trough;
   *  at 1 the two edges move together and the spout reads as a flat blade. */
  innerScale: number;
  /** How far the lip rises at the centre of the bulge, in profile units. */
  lift: number;
  /** Angular half-width — how far round the rim it tapers back to plain. */
  width: number;
  /** How far DOWN the wall the pull reaches, as a fraction of the vessel's
   *  height. A spout is not only a feature of the rim: the glass starts
   *  leaving the vertical below the lip, and without that the wall runs
   *  straight up and the beak turns out of it at a corner. */
  depth: number;
}

function flaskProfile(): VesselProfile {
  const HEIGHT = 1.34, BASE_R = 0.58, NECK_R = 0.13, NECK_START_T = 0.6;
  const taper = (t: number) => (t <= NECK_START_T ? BASE_R + (NECK_R - BASE_R) * (t / NECK_START_T) : NECK_R);
  // The cone taper starts exactly at t=0 (radius = BASE_R there too), so the
  // flat floor's edge met the sloped body at a razor-sharp corner. A cubic
  // Hermite blend over a short stretch right at the base rounds just that
  // corner — tangent to the flat (zero slope) at t=0 AND tangent to the
  // taper's own slope where it rejoins at CORNER_T, so the round lands on
  // the straight cone side with no kink (a plain eased blend matches the
  // taper's VALUE there but not its slope, which reads as a sharper elbow
  // than the original corner rather than a smooth round).
  const CORNER_T = 0.09;
  const taperSlope = (NECK_R - BASE_R) / NECK_START_T;
  const cornerEnd = taper(CORNER_T);
  return {
    height: HEIGHT, fillT: 0.44 * FILL_SHARE, hasFlatBase: true,
    standRadius: BASE_R, maxRadius: BASE_R, gripT: 0.8, collarT: 0.88, labelOffset: 0.32, spout: null,
    radiusAt: (t) => {
      if (t >= CORNER_T) return taper(t);
      const u = t / CORNER_T;
      const m1 = taperSlope * CORNER_T; // slope at u=1, in du terms (chain rule)
      const h01 = -2 * u ** 3 + 3 * u ** 2;
      const h11 = u ** 3 - u ** 2;
      const h00 = 1 - h01;
      return h00 * BASE_R + h01 * cornerEnd + h11 * m1;
    },
  };
}

function beakerProfile(): VesselProfile {
  // A Griffin low-form beaker is about 1.35 times as tall as it is wide, with
  // walls that are all but vertical. The old profile was neither — as wide as
  // it was tall, and flared 12% from base to rim — which is why it read as a
  // bucket.
  const HEIGHT = 1.18, BODY_R = 0.44, RIM_R = 0.455;
  // The other half of the bucket look was the base: a straight wall meeting a
  // flat floor at a bare right angle. Real glassware is blown, so the corner
  // is a fillet — a quarter circle tangent to the floor at y=0 and to the
  // wall at y=CORNER_R, which leaves the beaker standing on a foot slightly
  // narrower than its body, exactly as one does.
  const CORNER_R = 0.09;
  const FOOT_R = BODY_R - CORNER_R;
  const cornerT = CORNER_R / HEIGHT;
  const wall = (t: number) => BODY_R + (RIM_R - BODY_R) * ((t - cornerT) / (1 - cornerT));
  return {
    height: HEIGHT, fillT: 0.6 * FILL_SHARE, hasFlatBase: true,
    standRadius: BODY_R, maxRadius: RIM_R, gripT: 0.45, collarT: null, labelOffset: 0.3,
    // Dead left. It is the only angle where an outward bulge projects clear of
    // the silhouette: rotate it toward the viewer and it moves down and back
    // INTO the mouth's own near edge, and vanishes.
    spout: { angle: Math.PI, bump: 0.09, innerScale: 0.78, lift: 0.026, width: 0.9, depth: 0.1 },
    radiusAt: (t) => {
      if (t >= cornerT) return wall(t);
      const y = t * HEIGHT; // height above the floor, in profile units
      return FOOT_R + Math.sqrt(Math.max(0, CORNER_R * CORNER_R - (CORNER_R - y) ** 2));
    },
  };
}

function roundFlaskProfile(): VesselProfile {
  const SPHERE_R = 0.42, NECK_R = 0.105, NECK_H = 0.56;
  const HEIGHT = SPHERE_R * 2 + NECK_H;
  const sphereT = (SPHERE_R * 2) / HEIGHT;
  return {
    height: HEIGHT, fillT: sphereT * 0.55 * FILL_SHARE, hasFlatBase: false,
    standRadius: SPHERE_R, maxRadius: SPHERE_R, gripT: 0.86, collarT: 0.9, labelOffset: 0.3, spout: null,
    radiusAt: (t) => {
      if (t > sphereT) return NECK_R;
      const y = t * HEIGHT - SPHERE_R; // sphere-local y, [-R, R]
      return Math.sqrt(Math.max(0, SPHERE_R * SPHERE_R - y * y));
    },
  };
}

function testTubeProfile(): VesselProfile {
  // Half again as tall as it was. A tube seated in a rack loses its bottom
  // third behind the front rail, so the short version left barely a finger of
  // glass showing above the wood — and a test tube is the one vessel whose
  // whole point is a tall, narrow column of liquid you read down.
  const TUBE_R = 0.115, STRAIGHT_H = 1.05;
  const HEIGHT = STRAIGHT_H + TUBE_R;
  const capT = TUBE_R / HEIGHT;
  return {
    height: HEIGHT, fillT: 0.55 * FILL_SHARE, hasFlatBase: false,
    standRadius: TUBE_R, maxRadius: TUBE_R, gripT: 0.62, collarT: 0.95, labelOffset: 0.28, spout: null,
    radiusAt: (t) => {
      if (t >= capT) return TUBE_R;
      const y = t * HEIGHT - TUBE_R; // cap-local y, [-R, 0]
      return Math.sqrt(Math.max(0, TUBE_R * TUBE_R - y * y));
    },
  };
}

export const VESSEL_PROFILES: Record<VesselType, VesselProfile> = {
  flask: flaskProfile(),
  beaker: beakerProfile(),
  'round-flask': roundFlaskProfile(),
  'test-tube': testTubeProfile(),
};


/* ─────────────────────── How much a vessel is holding ───────────────────────
 *
 * The level a liquid stands at is not a share of the height: pour 25 mL into a
 * round flask and it sits deep and narrow in the sphere, where the same 25 mL
 * in a beaker is a shallow disc. Every vessel already declares the only thing
 * needed to say so — `radiusAt(t)` — so the volume below a height is the solid
 * of revolution under that profile, and the height for a volume is that same
 * function run backwards.
 *
 * Everything here works in PROFILE units (π and the height scale cancel), and
 * is anchored at the end: whatever the profile integrates to at `fillT` is
 * declared to be that vessel's workingML. So the bench looks exactly as it did
 * at the working volume, and every other amount is drawn relative to it.
 */

/** How finely the profile is sampled. The shapes are smooth and monotone in
 *  radius over any short span, so a couple of hundred trapezoids is well past
 *  the point where more changes a pixel. */
const VOLUME_SAMPLES = 240;

/** Cumulative ∫ r(u)² du from the floor, sampled once per vessel. */
const volumeTable = new Map<VesselType, number[]>();

function tableFor(type: VesselType): number[] {
  const cached = volumeTable.get(type);
  if (cached) return cached;
  const profile = VESSEL_PROFILES[type];
  const step = 1 / VOLUME_SAMPLES;
  const table: number[] = [0];
  let total = 0;
  let prev = profile.radiusAt(0) ** 2;
  for (let i = 1; i <= VOLUME_SAMPLES; i++) {
    const r2 = profile.radiusAt(i * step) ** 2;
    total += ((prev + r2) / 2) * step;
    table.push(total);
    prev = r2;
  }
  volumeTable.set(type, table);
  return table;
}

/** Where in the table a height falls, interpolated between samples. */
function profileVolumeAt(type: VesselType, t: number): number {
  const table = tableFor(type);
  const x = Math.min(1, Math.max(0, t)) * VOLUME_SAMPLES;
  const i = Math.min(VOLUME_SAMPLES - 1, Math.floor(x));
  return table[i] + (table[i + 1] - table[i]) * (x - i);
}

/** How many millilitres the vessel holds up to height `t`. */
export function volumeMLAt(type: VesselType, t: number): number {
  const profile = VESSEL_PROFILES[type];
  const atWorking = profileVolumeAt(type, profile.fillT);
  if (atWorking <= 0) return 0;
  return (profileVolumeAt(type, t) / atWorking) * WORKING_VOLUME_ML[type];
}

/**
 * The height a given volume stands at, as a fraction of the vessel's height.
 *
 * Bisected rather than solved: the shapes include a sphere and a cone with a
 * rounded corner, and cumulative volume is monotone in height for all of them,
 * which is the only property bisection needs. Clamped at the rim — a volume
 * past the vessel's capacity is the caller's problem to refuse (it is refused
 * in ChemistryBench), not something to draw as overflowing glass.
 */
export function fillTForVolumeML(type: VesselType, ml: number): number {
  if (ml <= 0) return 0;
  const profile = VESSEL_PROFILES[type];
  const target = (ml / WORKING_VOLUME_ML[type]) * profileVolumeAt(type, profile.fillT);
  if (target >= profileVolumeAt(type, 1)) return 1;
  let lo = 0, hi = 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (profileVolumeAt(type, mid) < target) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * How much this vessel holds to the brim — DERIVED, not declared.
 *
 * The bench's glassware is not to scale: FILL_SHARE deliberately draws less
 * liquid than a real vessel would hold at the same working volume, because a
 * beaker two thirds full reads as a glass of something rather than as an
 * experiment. Assert a capacity on top of that and the two disagree — the
 * first version of this claimed a 250 mL flask whose drawn shape held 149,
 * so a refusal at 250 would have been a refusal a student watched the glass
 * overflow through. What the vessel holds is whatever its own profile holds
 * above the anchor, and that is what a full vessel means here.
 */
export function brimVolumeML(type: VesselType): number {
  return volumeMLAt(type, 1);
}
